import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../api/client';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import AdminSessionGuard, { useAdminSession } from '../auth/AdminSessionGuard';
import { Loader } from '../components/ui/Loader';
import AdminLayout from '../layouts/AdminLayout';

const UserEntryTerminal = lazy(() => import('./UserEntryTerminal'));
const DelegatedEntryPage = lazy(() => import('./DelegatedEntryPage'));
const AdminDashboard = lazy(() => import('../pages/admin/dashboard/AdminDashboard'));
const CuttingPage = lazy(() => import('../pages/admin/dashboard/CuttingPage'));
const SalePurchasePage = lazy(() => import('../pages/admin/sale-purchase/SalePurchasePage'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const VoucherPage = lazy(() => import('../pages/admin/vouchers/VoucherPage'));
const AdminProfilePage = lazy(() => import('../pages/admin/accounts/AdminProfilePage'));
const AccountCreatePage = lazy(() => import('../pages/admin/accounts/AccountCreatePage'));
const AccountEditPage = lazy(() => import('../pages/admin/accounts/AccountEditPage'));
const AccountPaymentPage = lazy(() => import('../pages/admin/accounts/AccountPaymentPage'));
const BookDetailsPage = lazy(() => import('../pages/admin/accounts/BookDetailsPage'));
const DrawTimePage = lazy(() => import('../pages/admin/draw-time/DrawTimePage'));
const FileMergePage = lazy(() => import('../pages/admin/dashboard/FileMergePage'));
const ChangePasswordPage = lazy(() => import('../pages/admin/auth/ChangePasswordPage'));
const ReportsPage = lazy(() => import('../pages/admin/reports/ReportsPage'));
const DrawResultPage = lazy(() => import('../pages/admin/results/DrawResultPage'));
const BalanceUpdatePage = lazy(() => import('../pages/admin/balance/BalanceUpdatePage'));
const DataFilterPage = lazy(() => import('../pages/admin/data-filter/DataFilterPage'));
const LegacyApp = lazy(() => import('./App'));

function RouteLoader({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<Loader fullScreen label="Loading…" />}>
      {children}
    </Suspense>
  );
}

function LazyRoute({ children }: { children: ReactNode }) {
  return <RouteLoader>{children}</RouteLoader>;
}

function LegacyCompatibility() {
  return (
    <RouteLoader>
      <LegacyApp />
    </RouteLoader>
  );
}

function SalePurchaseRoleGuard() {
  const { session } = useAdminSession();
  return ['DISTRIBUTOR', 'MASTER', 'SUPER_MASTER'].includes(session.user.role)
    ? <LazyRoute><SalePurchasePage /></LazyRoute>
    : <Navigate to="/" replace />;
}

function CuttingAccessGuard(){
 const [allowed,setAllowed]=useState<boolean|null>(null);
 useEffect(()=>{let active=true;void apiRequest<{ok:true;allowed:boolean}>('/api/dashboard/cutting-access').then(r=>{if(active)setAllowed(r.allowed)}).catch(()=>{if(active)setAllowed(false)});return()=>{active=false}},[]);
 if(allowed===null)return <Loader label="Checking Cutting permission…"/>;
 return allowed?<LazyRoute><CuttingPage/></LazyRoute>:<Navigate to="/" replace/>;
}
function DashboardOrDelegatedEntry(){const location=useLocation();const {session}=useAdminSession();const params=new URLSearchParams(location.search);return params.get('page')==='cutting'?<CuttingAccessGuard/>:(params.get('page')==='delegated-entry'||(params.has('sharedPdf')&&['DISTRIBUTOR','MASTER','SUPER_MASTER'].includes(session.user.role)))?<DelegatedEntryGuard/>:<LazyRoute><AdminDashboard/></LazyRoute>}
function DelegatedEntryGuard() {
  const { session } = useAdminSession();
  return ['DISTRIBUTOR','MASTER','SUPER_MASTER'].includes(session.user.role) ? <LazyRoute><DelegatedEntryPage /></LazyRoute> : <Navigate to="/" replace />;
}
function AdminOnlyFileMerge() {const {session}=useAdminSession();return session.user.role==='ADMIN'?<LazyRoute><FileMergePage/></LazyRoute>:<Navigate to="/" replace/>;}
function AdminOnlyDrawTime() {
  const { session } = useAdminSession();
  return session.user.role === 'ADMIN' ? <LazyRoute><DrawTimePage /></LazyRoute> : <Navigate to="/" replace />;
}
function AdminOnlyDrawResult() {
  const { session } = useAdminSession();
  return session.user.role === 'ADMIN' ? <LazyRoute><DrawResultPage /></LazyRoute> : <Navigate to="/" replace />;
}
function AdminOnlyBalanceUpdate() {
  const { session } = useAdminSession();
  return session.user.role === 'ADMIN' ? <LazyRoute><BalanceUpdatePage /></LazyRoute> : <Navigate to="/" replace />;
}
function AdminOnlyDataFilter() {
  const { session } = useAdminSession();
  return session.user.role === 'ADMIN' ? <LazyRoute><DataFilterPage /></LazyRoute> : <Navigate to="/" replace />;
}

export const router = createBrowserRouter([
  {
    path: '/login/*',
    element: <LazyRoute><LoginPage /></LazyRoute>,
  },
  {
    path: '/',
    element: <AdminSessionGuard fallback={<LazyRoute><UserEntryTerminal /></LazyRoute>} />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <DashboardOrDelegatedEntry /> },
          { path: 'sale-purchase/', element: <SalePurchaseRoleGuard /> },
          { path: 'accounts/new/', element: <LazyRoute><AccountCreatePage /></LazyRoute> },
          { path: 'accounts/:id/edit/', element: <LazyRoute><AccountEditPage /></LazyRoute> },
          { path: 'accounts/:id/payment/', element: <LazyRoute><AccountPaymentPage /></LazyRoute> },
          { path: 'payment-details/', element: <LazyRoute><BookDetailsPage /></LazyRoute> },
          { path: 'draw-time/', element: <AdminOnlyDrawTime /> },
          { path: 'file-merge/', element: <AdminOnlyFileMerge /> },
          { path: 'draw-result/', element: <AdminOnlyDrawResult /> },
          { path: 'balance-update/', element: <AdminOnlyBalanceUpdate /> },
          { path: 'data-filter/', element: <AdminOnlyDataFilter /> },
          { path: 'vouchers/', element: <LazyRoute><VoucherPage /></LazyRoute> },
          { path: 'cutting/', element: <CuttingAccessGuard /> },
          { path: 'delegated-entry/', element: <DelegatedEntryGuard /> },
          { path: 'admin-profile/', element: <LazyRoute><AdminProfilePage /></LazyRoute> },
          { path: 'change-password/', element: <LazyRoute><ChangePasswordPage /></LazyRoute> },
          { path: 'hierarchy-reports/', element: <LazyRoute><ReportsPage /></LazyRoute> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <LegacyCompatibility />,
  },
]);

export default router;
